import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { cloneElement } from 'react';
import { htmlToJsx } from 'html-to-jsx-transform';
import remarkFrontmatter from 'remark-frontmatter';
import { remark } from 'remark';
import remarkGfm from 'remark-gfm';
import remarkMdx from 'remark-mdx';
import { Fragment } from 'react';
const mdxParser = remark()
    .use(remarkFrontmatter, ['yaml', 'toml'])
    .use(remarkGfm)
    .use(remarkMdx);
void React;
export function SafeMdxRenderer({ components, code = '', mdast = null, }) {
    const visitor = new MdastToJsx({ code, mdast, components });
    const result = visitor.run();
    return result;
}
const nativeTags = [
    'blockquote',
    'strong',
    'em',
    'del',
    'hr',
    'a',
    'b',
    'br',
    'button',
    'div',
    'form',
    'h1',
    'h2',
    'h3',
    'h4',
    'head',
    'iframe',
    'img',
    'input',
    'label',
    'li',
    'link',
    'ol',
    'p',
    'path',
    'picture',
    'script',
    'section',
    'source',
    'span',
    'sub',
    'sup',
    'svg',
    'table',
    'tbody',
    'td',
    'th',
    'thead',
    'tr',
    'ul',
    'video',
    'code',
    'pre',
];
export class MdastToJsx {
    constructor({ code = '', mdast = undefined, components = {}, }) {
        this.jsxStr = '';
        this.errors = [];
        this.str = code;
        this.mdast = mdast || mdxParser.parse(code);
        this.c = Object.assign(Object.assign({}, Object.fromEntries(nativeTags.map((tag) => {
            return [tag, tag];
        }))), components);
    }
    mapMdastChildren(node) {
        var _a;
        const res = (_a = node.children) === null || _a === void 0 ? void 0 : _a.flatMap((child) => this.mdastTransformer(child)).filter(Boolean);
        if (Array.isArray(res)) {
            if (!res.length) {
                return null;
            }
            else if (res.length === 1) {
                return res[0];
            }
            else {
                return res.map((x, i) => React.isValidElement(x) ? cloneElement(x, { key: i }) : x);
            }
        }
        return res || null;
    }
    mapJsxChildren(node) {
        var _a;
        const res = (_a = node.children) === null || _a === void 0 ? void 0 : _a.flatMap((child, i) => this.jsxTransformer(child)).filter(Boolean);
        if (Array.isArray(res)) {
            if (!res.length) {
                return null;
            }
            else if (res.length === 1) {
                return res[0];
            }
            else {
                return res.map((x, i) => React.isValidElement(x) ? cloneElement(x, { key: i }) : x);
            }
        }
        return res || null;
    }
    jsxTransformer(node) {
        if (!node) {
            return [];
        }
        switch (node.type) {
            case 'mdxJsxTextElement':
            case 'mdxJsxFlowElement': {
                if (!node.name) {
                    return [];
                }
                const Component = accessWithDot(this.c, node.name);
                if (!Component) {
                    this.errors.push({
                        message: `Unsupported jsx component ${node.name}`,
                    });
                    return null;
                }
                let attrsList = getJsxAttrs(node, (err) => {
                    this.errors.push(err);
                });
                let attrs = Object.fromEntries(attrsList);
                return (_jsx(Component, Object.assign({}, attrs, { children: this.mapJsxChildren(node) })));
            }
            default: {
                return this.mdastTransformer(node);
            }
        }
    }
    run() {
        const res = this.mdastTransformer(this.mdast);
        if (Array.isArray(res) && res.length === 1) {
            return res[0];
        }
        return res;
    }
    mdastTransformer(node) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
        if (!node) {
            return [];
        }
        switch (node.type) {
            case 'mdxjsEsm': {
                const start = (_b = (_a = node.position) === null || _a === void 0 ? void 0 : _a.start) === null || _b === void 0 ? void 0 : _b.offset;
                const end = (_d = (_c = node.position) === null || _c === void 0 ? void 0 : _c.end) === null || _d === void 0 ? void 0 : _d.offset;
                let text = this.str.slice(start, end);
                return [];
            }
            case 'mdxJsxTextElement':
            case 'mdxJsxFlowElement': {
                const start = (_f = (_e = node.position) === null || _e === void 0 ? void 0 : _e.start) === null || _f === void 0 ? void 0 : _f.offset;
                const end = (_h = (_g = node.position) === null || _g === void 0 ? void 0 : _g.end) === null || _h === void 0 ? void 0 : _h.offset;
                const text = this.str.slice(start, end);
                try {
                    this.jsxStr = text;
                    const result = this.jsxTransformer(node);
                    if (Array.isArray(result)) {
                        console.log(`Unexpected array result`);
                    }
                    else if (result) {
                        return result;
                    }
                }
                finally {
                    this.jsxStr = '';
                }
                return [];
            }
            case 'mdxFlowExpression':
            case 'mdxTextExpression': {
                if (!node.value) {
                    return [];
                }
                return [];
            }
            case 'yaml': {
                if (!node.value) {
                    return [];
                }
                return [];
            }
            case 'heading': {
                const level = node.depth;
                const Tag = `h${level}`;
                return _jsx(Tag, { children: this.mapMdastChildren(node) });
            }
            case 'paragraph': {
                return _jsx(this.c.p, { children: this.mapMdastChildren(node) });
            }
            case 'blockquote': {
                return (_jsx(this.c.blockquote, { children: this.mapMdastChildren(node) }));
            }
            case 'thematicBreak': {
                return _jsx(this.c.hr, {});
            }
            case 'code': {
                if (!node.value) {
                    return [];
                }
                const language = node.lang || '';
                const code = node.value;
                return (_jsx(this.c.pre, { children: _jsx(this.c.code, { children: code }) }));
            }
            case 'list': {
                if (node.ordered) {
                    return (_jsx(this.c.ol, { start: node.start, children: this.mapMdastChildren(node) }));
                }
                return _jsx(this.c.ul, { children: this.mapMdastChildren(node) });
            }
            case 'listItem': {
                // https://github.com/syntax-tree/mdast-util-gfm-task-list-item#syntax-tree
                if ((node === null || node === void 0 ? void 0 : node.checked) != null) {
                    return (_jsx(this.c.li, { "data-checked": node.checked, children: this.mapMdastChildren(node) }));
                }
                return _jsx(this.c.li, { children: this.mapMdastChildren(node) });
            }
            case 'text': {
                if (!node.value) {
                    return [];
                }
                return node.value;
            }
            case 'image': {
                const src = node.url || '';
                const alt = node.alt || '';
                const title = node.title || '';
                return _jsx(this.c.img, { src: src, alt: alt, title: title });
            }
            case 'link': {
                const href = node.url || '';
                const title = node.title || '';
                return (_jsx(this.c.a, { href, title, children: this.mapMdastChildren(node) }));
            }
            case 'strong': {
                return (_jsx(this.c.strong, { children: this.mapMdastChildren(node) }));
            }
            case 'emphasis': {
                return _jsx(this.c.em, { children: this.mapMdastChildren(node) });
            }
            case 'delete': {
                return _jsx(this.c.del, { children: this.mapMdastChildren(node) });
            }
            case 'inlineCode': {
                if (!node.value) {
                    return [];
                }
                return _jsx(this.c.code, { children: node.value });
            }
            case 'break': {
                return _jsx(this.c.br, {});
            }
            case 'root': {
                return _jsx(Fragment, { children: this.mapMdastChildren(node) });
            }
            case 'table': {
                const [head, ...body] = React.Children.toArray(this.mapMdastChildren(node));
                return (_jsxs(this.c.table, { children: [head && _jsx(this.c.thead, { children: head }), !!(body === null || body === void 0 ? void 0 : body.length) && _jsx(this.c.tbody, { children: body })] }));
            }
            case 'tableRow': {
                return (_jsx(this.c.tr, { className: '', children: this.mapMdastChildren(node) }));
            }
            case 'tableCell': {
                let content = this.mapMdastChildren(node);
                return _jsx(this.c.td, { className: '', children: content });
            }
            case 'definition': {
                return [];
            }
            case 'linkReference': {
                let href = '';
                mdastBfs(this.mdast, (child) => {
                    if (child.type === 'definition' &&
                        child.identifier === node.identifier) {
                        href = child.url;
                    }
                });
                return (_jsx(this.c.a, { href: href, children: this.mapMdastChildren(node) }));
            }
            case 'footnoteReference': {
                return [];
            }
            case 'footnoteDefinition': {
                return [];
            }
            case 'html': {
                const start = (_k = (_j = node.position) === null || _j === void 0 ? void 0 : _j.start) === null || _k === void 0 ? void 0 : _k.offset;
                const end = (_m = (_l = node.position) === null || _l === void 0 ? void 0 : _l.end) === null || _m === void 0 ? void 0 : _m.offset;
                const text = this.str.slice(start, end);
                if (!text) {
                    return [];
                }
                const jsx = htmlToJsx(text);
                try {
                    this.jsxStr = jsx;
                    const result = this.jsxTransformer(node);
                    if (Array.isArray(result)) {
                        console.log(`Unexpected array result`);
                    }
                    else if (result) {
                        return result;
                    }
                }
                finally {
                    this.jsxStr = '';
                }
                return [];
            }
            case 'imageReference': {
                return [];
            }
            default: {
                mdastBfs(node, (node) => {
                    delete node.position;
                });
                throw new Error(`cannot convert node` + JSON.stringify(node, null, 2));
                return [];
            }
        }
    }
}
export function getJsxAttrs(node, onError = console.error) {
    let attrsList = node.attributes
        .map((attr) => {
        if (attr.type === 'mdxJsxExpressionAttribute') {
            onError({
                message: `Expressions in jsx props are not supported (${attr.value.replace(/\n+/g, ' ')})`,
            });
            return;
        }
        if (attr.type !== 'mdxJsxAttribute') {
            throw new Error(`non mdxJsxAttribute is not supported: ${attr}`);
        }
        const v = attr.value;
        if (typeof v === 'string' || typeof v === 'number') {
            return [attr.name, v];
        }
        if (v === null) {
            return [attr.name, true];
        }
        if ((v === null || v === void 0 ? void 0 : v.type) === 'mdxJsxAttributeValueExpression') {
            if (v.value === 'true') {
                return [attr.name, true];
            }
            if (v.value === 'false') {
                return [attr.name, false];
            }
            if (v.value === 'null') {
                return [attr.name, null];
            }
            if (v.value === 'undefined') {
                return [attr.name, undefined];
            }
            let quote = ['"', "'", '`'].find((q) => v.value.startsWith(q) && v.value.endsWith(q));
            if (quote) {
                let value = v.value;
                if (quote !== '"') {
                    value = v.value.replace(new RegExp(quote, 'g'), '"');
                }
                return [attr.name, JSON.parse(value)];
            }
            const number = Number(v.value);
            if (!isNaN(number)) {
                return [attr.name, number];
            }
            const parsedJson = safeJsonParse(v.value);
            if (parsedJson) {
                return [attr.name, parsedJson];
            }
            onError({
                message: `Expressions in jsx props are not supported (${attr.name}={${v.value}})`,
            });
        }
        else {
            console.log('unhandled attr', { attr }, attr.type);
        }
        return;
    })
        .filter(isTruthy);
    return attrsList;
}
function isTruthy(val) {
    return Boolean(val);
}
function accessWithDot(obj, path) {
    return path
        .split('.')
        .map((x) => x.trim())
        .filter(Boolean)
        .reduce((o, i) => o[i], obj);
}
export function mdastBfs(node, cb) {
    const queue = [node];
    const result = [];
    while (queue.length) {
        const node = queue.shift();
        let r = cb && node ? cb(node) : node;
        if (Array.isArray(r)) {
            queue.push(...r);
        }
        else if (r) {
            result.push(r);
        }
        if (node && 'children' in node && node.children) {
            queue.push(...node.children);
        }
    }
    return result;
}
function safeJsonParse(str) {
    try {
        return JSON.parse(str);
    }
    catch (err) {
        return null;
    }
}
//# sourceMappingURL=safe-mdx.js.map